import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, TrendingUp, TrendingDown, DollarSign, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LossRatioReportPage() {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/programs/reports/loss-ratio?days=${days}`, { headers: auth });
      if (!r.ok) { setData({ error: 'Admin only' }); return; }
      setData(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (data?.error) return <div className="p-8 text-red-600">{data.error}</div>;

  const ratio = data.overall_loss_ratio_pct;
  const ratioColor = ratio < 40 ? 'text-emerald-600' : ratio < 70 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="loss-ratio-page">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-indigo-500" /> Loss Ratio Report
          </h1>
          <p className="text-slate-500 mt-1">Claims paid ÷ protection revenue · rolling window.</p>
        </div>
        <div className="flex gap-2">
          {[30, 90, 180, 365].map(d => (
            <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} onClick={() => setDays(d)} data-testid={`days-${d}`}>
              {d}d
            </Button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Protection revenue</div>
          <div className="text-2xl font-bold tabular-nums text-emerald-600 mt-1">${data.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Claims paid</div>
          <div className="text-2xl font-bold tabular-nums text-red-600 mt-1">${data.total_claims_paid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Claim count</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{data.claim_count}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Overall loss ratio</div>
          <div className={`text-2xl font-bold tabular-nums mt-1 ${ratioColor}`}>{ratio}%</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Revenue by Program</CardTitle><CardDescription>{data.reports_count} reports issued in the last {data.period_days} days</CardDescription></CardHeader>
        <CardContent>
          {Object.keys(data.revenue_by_program).length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No protection reports issued in this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 border-b"><th className="text-left py-2">Program / Tier</th><th className="text-right">Revenue</th><th className="text-right">Share</th></tr></thead>
              <tbody>
                {Object.entries(data.revenue_by_program).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <tr key={k} className="border-b last:border-b-0">
                    <td className="py-2 font-mono text-xs">{k}</td>
                    <td className="text-right tabular-nums">${v.toFixed(2)}</td>
                    <td className="text-right text-slate-500 text-xs">{data.total_revenue ? ((v / data.total_revenue) * 100).toFixed(1) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-4 h-4" />Top Merchants by Protection Revenue</CardTitle></CardHeader>
        <CardContent>
          {Object.keys(data.revenue_by_merchant).length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No merchant activity in this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 border-b"><th className="text-left py-2">Merchant ID</th><th className="text-right">Revenue</th></tr></thead>
              <tbody>
                {Object.entries(data.revenue_by_merchant).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([k, v]) => (
                  <tr key={k} className="border-b last:border-b-0">
                    <td className="py-2 font-mono text-xs">{k.slice(0, 8)}…</td>
                    <td className="text-right tabular-nums">${v.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {ratio > 70 && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="text-sm">
              <b className="text-red-800">Loss ratio above 70% — review pricing and underwriting</b>
              <div className="text-red-700 mt-1">Consider tightening AP approvals for high-value commodities, or increasing merchant retention on SelleShare.</div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
